/**
 * Owner Brand Settings — name, description, color, threshold, logo & banner.
 */
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Trash2, ArrowUp, ArrowDown, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { assertBrandMatch, formatRupiah } from '@/lib/utils';
import {
  PageHeader, SectionCard, FullSpinner, QueryError,
} from '@/components/ui_primitives/Misc';
import Button from '@/components/ui_primitives/Button';
import { FieldRow, Input, Textarea, ColorField } from '@/components/ui_primitives/Field';
import ImageUploader from '@/components/ui_primitives/ImageUploader';

// EX-A.2 — Presentation & social URL validators.
// Mirror backend regexes in /app/backend/models.py + owner.py/admin.py:
//   • instagram_url: strict ^https://(www\.)?instagram\.com/.+
//   • website_url:   http/https,  any host
//   • stamp_icon_url / card_background_image_url: no regex (accept relative
//     upload paths like "/api/files/..."), max length 2000 — matches backend
// All four accept empty string from the input and transform to null on
// submit so the dirtyFields-only PATCH sends a clean clear.
const INSTAGRAM_RE = /^https:\/\/(www\.)?instagram\.com\/.+/;
const WEBSITE_RE = /^https?:\/\/.+/;

const optInstagramUrl = z
  .string()
  .trim()
  .max(200, 'Max 200 characters')
  .transform((v) => (v === '' ? null : v))
  .refine(
    (v) => v === null || INSTAGRAM_RE.test(v),
    { message: 'Must be a full https://instagram.com/ profile URL' },
  );

const optWebsiteUrl = z
  .string()
  .trim()
  .max(200, 'Max 200 characters')
  .transform((v) => (v === '' ? null : v))
  .refine(
    (v) => v === null || WEBSITE_RE.test(v),
    { message: 'Must start with http:// or https://' },
  );

const optUploadUrl = z
  .string()
  .trim()
  .max(2000)
  .transform((v) => (v === '' ? null : v));

const schema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().min(1).max(1000),
  primary_color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use #RRGGBB hex'),
  stamp_threshold_rupiah: z.coerce.number().int().min(1).max(10_000_000),
  receipt_upload_required: z.boolean().default(false),
  // EX-A Step 5 — loyalty engine controls. All optional client-side; backend
  // enforces the positive guard when earning_method is "visit". Visit fields
  // remain editable while earning_method is "spend" (configured but inert)
  // so owners can prepare a future switch.
  earning_method: z.enum(['spend', 'visit']).default('spend'),
  stamps_per_visit: z.coerce.number().int().min(1).max(100).default(1),
  visit_minimum_spend_rupiah: z.coerce.number().int().min(0).max(10_000_000).default(0),
  max_stamps_per_day: z.coerce.number().int().min(0).max(1000).default(0),
  // EX-A.2 — Brand presentation & social. Empty string → null on submit so
  // the dirtyFields-only PATCH can clear a previously set value cleanly.
  stamp_icon_url: optUploadUrl,
  card_background_image_url: optUploadUrl,
  instagram_url: optInstagramUrl,
  website_url: optWebsiteUrl,
});

export default function OwnerSettingsPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const qc = useQueryClient();

  const brandQ = useQuery({
    queryKey: qk.ownerBrand(brandId),
    queryFn: async () => assertBrandMatch(await api.ownerBrand(token), brandId, 'id'),
    enabled: !!token && !!brandId,
  });

  const {
    register, handleSubmit, watch, setValue, reset,
    formState: { errors, isSubmitting, isDirty, dirtyFields },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '', description: '', primary_color: '#C9A84C', stamp_threshold_rupiah: 50_000, receipt_upload_required: false,
      earning_method: 'spend', stamps_per_visit: 1, visit_minimum_spend_rupiah: 0, max_stamps_per_day: 0,
      // EX-A.2 — presentation & social (all default empty for input compat).
      stamp_icon_url: '', card_background_image_url: '', instagram_url: '', website_url: '',
    },
  });
  const [logo, setLogo] = useState('');
  const [banner, setBanner] = useState('');
  const [imgDirty, setImgDirty] = useState(false);
  // Phase W-3 — categories live outside RHF (multi-select with a checkbox
  // grid). Mirror the dirty-tracking pattern used by `logo` / `banner`
  // so dirtyFields-only PATCH semantics still hold.
  const [categoryIds, setCategoryIds] = useState([]);
  const [catsDirty, setCatsDirty] = useState(false);
  const categoriesQ = useQuery({
    queryKey: qk.categories(),
    queryFn: () => api.categories(token),
    enabled: !!token,
    // Refresh when the page mounts so a newly-added admin category
    // shows up without requiring a hard refresh.
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (!brandQ.data) return;
    reset({
      name: brandQ.data.name ?? '',
      description: brandQ.data.description ?? '',
      primary_color: brandQ.data.primary_color ?? '#C9A84C',
      stamp_threshold_rupiah: brandQ.data.stamp_threshold_rupiah ?? 50_000,
      receipt_upload_required: !!brandQ.data.receipt_upload_required,
      // EX-A — loyalty engine controls (defaults preserve legacy spend mode).
      earning_method: brandQ.data.earning_method ?? 'spend',
      stamps_per_visit: brandQ.data.stamps_per_visit ?? 1,
      visit_minimum_spend_rupiah: brandQ.data.visit_minimum_spend_rupiah ?? 0,
      max_stamps_per_day: brandQ.data.max_stamps_per_day ?? 0,
      // EX-A.2 — presentation & social (null/undefined from API → '' for input).
      stamp_icon_url: brandQ.data.stamp_icon_url ?? '',
      card_background_image_url: brandQ.data.card_background_image_url ?? '',
      instagram_url: brandQ.data.instagram_url ?? '',
      website_url: brandQ.data.website_url ?? '',
    });
    setLogo(brandQ.data.logo_url ?? '');
    setBanner(brandQ.data.banner_url ?? '');
    setImgDirty(false);
    setCategoryIds(brandQ.data.category_ids ?? []);
    setCatsDirty(false);
  }, [brandQ.data, reset]);

  const save = useMutation({
    mutationFn: (body) => api.ownerPatchBrand(token, body),
    onSuccess: () => {
      toast.success('Brand settings saved');
      qc.invalidateQueries({ queryKey: qk.ownerBrand(brandId) });
    },
    onError: (e) => toast.error(String(e?.detail ?? 'Save failed')),
  });

  const color = watch('primary_color');
  const threshold = Number(watch('stamp_threshold_rupiah')) || 0;
  // EX-A Step 5 — drive the segmented control + disabled states for the
  // loyalty engine card. Visit fields stay editable while mode is "spend"
  // so owners can pre-configure a future switch; the backend treats them
  // as inert until earning_method is flipped to "visit".
  const earningMethod = watch('earning_method') || 'spend';
  const isVisit = earningMethod === 'visit';
  const stampsPerVisit = Number(watch('stamps_per_visit')) || 1;
  const visitMinSpend = Number(watch('visit_minimum_spend_rupiah')) || 0;
  const dailyCap = Number(watch('max_stamps_per_day')) || 0;

  if (brandQ.isLoading) return <FullSpinner />;
  if (brandQ.error) return <QueryError error={brandQ.error} onRetry={() => brandQ.refetch()} />;

  const onSubmit = (vals) => {
    // EX-A Step 5 — Send ONLY changed fields so the backend's targeted
    // `brand.loyalty_config_change` audit fires precisely when one of the
    // 5 loyalty fields actually mutated. Images are tracked outside RHF.
    const body = {};
    for (const key of Object.keys(dirtyFields || {})) {
      if (dirtyFields[key]) body[key] = vals[key];
    }
    if (imgDirty) {
      body.logo_url = logo;
      body.banner_url = banner;
    }
    if (catsDirty) {
      // Phase W-3 — submit even an empty array; backend auto-fills [Other].
      body.category_ids = categoryIds;
    }
    if (Object.keys(body).length === 0) return; // nothing to save
    save.mutate(body);
  };

  return (
    <div className="space-y-6" data-testid="owner-settings-page">
      <PageHeader
        kicker="Brand Settings"
        title="Your brand identity"
        subtitle="Name, color and threshold customers see throughout the app."
        actions={
          <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting || save.isPending}
            leftIcon={<Save size={14} />}
            disabled={!isDirty && !imgDirty && !catsDirty}
            testID="settings-save">
            Save changes
          </Button>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard className="lg:col-span-2 space-y-5" testID="settings-form">
          <FieldRow label="Brand name" required error={errors.name?.message}>
            <Input {...register('name')} />
          </FieldRow>
          <FieldRow label="Description" required error={errors.description?.message}
            hint="Shown on the brand detail page and customer cards.">
            <Textarea rows={4} {...register('description')} />
          </FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Primary color" required error={errors.primary_color?.message}>
              <ColorField value={color} {...register('primary_color')}
                onChange={(e) => setValue('primary_color', e.target.value, { shouldDirty: true })} />
            </FieldRow>
            <FieldRow label="Stamp threshold (Rp)"
              required={!isVisit}
              error={errors.stamp_threshold_rupiah?.message}
              hint={isVisit
                ? 'Used only in “Per spend” mode'
                : `= 1 stamp per ${formatRupiah(threshold)}`}>
              <Input type="number" min={1} step={500}
                disabled={isVisit}
                data-testid="settings-stamp-threshold-input"
                {...register('stamp_threshold_rupiah')} />
            </FieldRow>
          </div>
        </SectionCard>

        <SectionCard className="space-y-5" testID="settings-media">
          <FieldRow label="Logo" hint="Square recommended (≥ 256×256)">
            <ImageUploader value={logo} onChange={(v) => { setLogo(v); setImgDirty(true); }} aspect="square" />
          </FieldRow>
          <FieldRow label="Banner" hint="3:1 ratio recommended (≥ 1200×400)">
            <ImageUploader value={banner} onChange={(v) => { setBanner(v); setImgDirty(true); }} aspect="banner" />
          </FieldRow>
        </SectionCard>
      </div>

      {/* EX-A Step 5 — Earning method (Per spend vs Per visit) + Daily cap.
          Visit fields stay editable while mode is "spend" so owners can
          pre-configure a future switch; backend treats them as inert until
          earning_method is flipped to "visit". */}
      <SectionCard
        title="Earning method"
        subtitle="How customers earn stamps. You can pre-configure visit-based settings while staying in “Per spend” — they stay inert until you switch."
        testID="settings-earning-method"
      >
        <div className="space-y-5">
          {/* Segmented control */}
          <FieldRow label="How customers earn stamps" required>
            <div
              role="radiogroup"
              aria-label="Earning method"
              className="inline-flex rounded-2xl border border-white/10 bg-black/40 p-1"
              data-testid="earning-method-segmented"
            >
              {[
                { value: 'spend', label: 'Per spend' },
                { value: 'visit', label: 'Per visit' },
              ].map((opt) => {
                const selected = earningMethod === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() =>
                      setValue('earning_method', opt.value, { shouldDirty: true })
                    }
                    className={
                      'px-4 py-1.5 text-xs font-semibold rounded-xl transition-colors ' +
                      (selected
                        ? 'bg-gold/15 text-gold shadow-glow-gold-sm'
                        : 'text-white/55 hover:text-white/80')
                    }
                    data-testid={`earning-method-radio-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] text-white/40">
              Visit-based: customers earn a fixed number of stamps per qualifying visit, regardless of spend above the minimum.
            </p>
          </FieldRow>

          <div className="grid md:grid-cols-2 gap-4">
            <FieldRow label="Stamps per visit"
              required={isVisit}
              error={errors.stamps_per_visit?.message}
              hint={isVisit
                ? `Awards ${stampsPerVisit} stamp${stampsPerVisit === 1 ? '' : 's'} per qualifying visit`
                : "Configurable; takes effect only when 'Per visit' is selected."}>
              <Input type="number" min={1} max={100} step={1}
                disabled={!isVisit}
                data-testid="stamps-per-visit-input"
                {...register('stamps_per_visit')} />
            </FieldRow>

            <FieldRow label="Visit minimum spend (Rp)"
              error={errors.visit_minimum_spend_rupiah?.message}
              hint={isVisit
                ? (visitMinSpend > 0
                    ? `Customer must spend at least ${formatRupiah(visitMinSpend)} to earn`
                    : 'No minimum spend required to earn')
                : "Configurable; takes effect only when 'Per visit' is selected."}>
              <Input type="number" min={0} max={10_000_000} step={1000}
                disabled={!isVisit}
                data-testid="visit-min-spend-input"
                {...register('visit_minimum_spend_rupiah')} />
            </FieldRow>
          </div>

          <FieldRow label="Max stamps per day (per customer)"
            error={errors.max_stamps_per_day?.message}
            hint={dailyCap > 0
              ? `Each customer can earn up to ${dailyCap} stamp${dailyCap === 1 ? '' : 's'} per day`
              : '0 = unlimited (no daily cap)'}>
            <Input type="number" min={0} max={1000} step={1}
              data-testid="max-stamps-per-day-input"
              {...register('max_stamps_per_day')} />
          </FieldRow>
        </div>
      </SectionCard>

      {/* EX-A.2 — Brand presentation & social. All four fields optional;
          empty value clears (null on backend). dirtyFields-only PATCH
          ensures `brand.presentation_change` audit fires precisely. */}
      <SectionCard
        title="Brand presentation & social"
        subtitle="Custom stamp icon, stamp card background and public social links. All optional — leave empty to use the OneStamp defaults."
        testID="settings-presentation"
      >
        <div className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <FieldRow label="Stamp icon"
              hint="Square recommended (≥ 640×640). Replaces the default gold dot on filled stamps."
              error={errors.stamp_icon_url?.message}>
              <ImageUploader
                value={watch('stamp_icon_url') || ''}
                onChange={(v) => setValue('stamp_icon_url', v, { shouldDirty: true })}
                aspect="square"
                testID="settings-stamp-icon-uploader"
              />
            </FieldRow>
            <FieldRow label="Stamp card background"
              hint="4:3 ratio recommended (≥ 1280×960). Shown behind the card progress on customer surfaces."
              error={errors.card_background_image_url?.message}>
              <ImageUploader
                value={watch('card_background_image_url') || ''}
                onChange={(v) => setValue('card_background_image_url', v, { shouldDirty: true })}
                aspect="card"
                testID="settings-card-bg-uploader"
              />
            </FieldRow>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <FieldRow label="Instagram URL"
              hint="Full profile URL, e.g. https://instagram.com/yourbrand"
              error={errors.instagram_url?.message}>
              <Input
                type="url"
                placeholder="https://instagram.com/yourbrand"
                maxLength={200}
                data-testid="settings-instagram-url-input"
                {...register('instagram_url')}
              />
            </FieldRow>
            <FieldRow label="Website URL"
              hint="Public website. Must start with http:// or https://"
              error={errors.website_url?.message}>
              <Input
                type="url"
                placeholder="https://yourbrand.com"
                maxLength={200}
                data-testid="settings-website-url-input"
                {...register('website_url')}
              />
            </FieldRow>
          </div>
        </div>
      </SectionCard>

      {/* Phase W-3 — Categories. Multi-select against the admin-managed
          taxonomy. Lives outside RHF; tracked via local `categoryIds` +
          `catsDirty` mirrors of the imgDirty pattern so dirtyFields-only
          PATCH semantics still hold. Empty submission → backend auto-
          fills to ["Other"] (handled server-side, refresh reveals). */}
      <SectionCard
        title="Categories"
        subtitle="Select one or more categories that describe your brand. Customers can filter by category on the All Brands page. Leave all unchecked and your brand will be tagged as 'Other' automatically."
        testID="owner-settings-categories-section"
      >
        {categoriesQ.isLoading ? (
          <div className="h-24 rounded-xl bg-white/[0.03] border border-white/5 animate-pulse" />
        ) : (
          <>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
              data-testid="owner-settings-categories-grid"
            >
              {(categoriesQ.data ?? [])
                .slice()
                .sort(
                  (a, b) =>
                    (a.sort_index - b.sort_index) || a.name.localeCompare(b.name),
                )
                .map((c) => {
                  const on = categoryIds.includes(c.id);
                  return (
                    <label
                      key={c.id}
                      data-testid={`owner-settings-category-option-${c.slug}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors text-sm ${
                        on
                          ? 'bg-gold/10 border-gold/45 text-white'
                          : 'bg-[#1A1A1A] border-white/8 text-white/80 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="accent-gold w-4 h-4"
                        checked={on}
                        onChange={() => {
                          setCategoryIds((prev) =>
                            prev.includes(c.id)
                              ? prev.filter((x) => x !== c.id)
                              : [...prev, c.id],
                          );
                          setCatsDirty(true);
                        }}
                      />
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: c.color || '#9CA3AF' }}
                      />
                      <span className="truncate">{c.name}</span>
                      <span className="ml-auto font-mono text-[10px] text-white/35">
                        {c.slug}
                      </span>
                    </label>
                  );
                })}
            </div>
            {categoryIds.length === 0 ? (
              <div
                className="mt-3 px-3 py-2.5 rounded-xl bg-amber-500/10 border border-amber-400/30 text-amber-200 text-xs leading-relaxed"
                data-testid="owner-settings-categories-empty-note"
              >
                No categories selected — your brand will be tagged{' '}
                <strong>'Other'</strong> automatically when you save.
              </div>
            ) : null}
          </>
        )}
      </SectionCard>
      <SectionCard
        title="Gallery Photos"
        subtitle="Up to 10 photos shown on the customer brand page (drag-style reorder, activate/deactivate, delete). Active photos appear in order; inactive ones stay hidden from customers."
        testID="settings-gallery"
      >
        <GallerySection brandId={brandId} token={token} />
      </SectionCard>

      <SectionCard
        title="Cashier policy"
        subtitle="Controls what cashiers must do for every transaction."
        testID="settings-cashier-policy"
      >
        <label className="flex items-start gap-3 cursor-pointer" data-testid="settings-receipt-required">
          <input
            type="checkbox"
            className="accent-gold w-4 h-4 mt-1"
            {...register('receipt_upload_required')}
          />
          <div className="flex-1">
            <div className="text-sm font-semibold text-white">
              Require receipt upload
            </div>
            <p className="text-xs text-white/55 mt-1 leading-5">
              Cashiers must attach a photo of the printed receipt before
              submitting a transaction. Helps reconcile sales with stamps and
              prevents fraudulent issuance.
            </p>
          </div>
        </label>
      </SectionCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gallery Photos — owner-managed, customer-visible. Local component to keep
// the existing settings-form/save-button flow simple (no new pages).
// ---------------------------------------------------------------------------
const GALLERY_LIMIT = 10;
const ACCEPTED_MIMES = ['image/png', 'image/jpeg', 'image/webp'];

function GallerySection({ brandId, token }) {
  const qc = useQueryClient();
  const fileInputRef = useRef(null);

  const galleryQ = useQuery({
    queryKey: qk.ownerGallery(brandId),
    queryFn: () => api.ownerGalleryList(token, brandId),
    enabled: !!token && !!brandId,
  });

  const refetch = () => qc.invalidateQueries({ queryKey: qk.ownerGallery(brandId) });

  const uploadMut = useMutation({
    mutationFn: (file) => api.ownerGalleryUpload(token, brandId, file),
    onSuccess: () => { toast.success('Photo added'); refetch(); },
    onError: (e) => {
      const code = e?.detail?.code;
      if (code === 'gallery_limit_reached') {
        toast.error(`Limit reached — ${GALLERY_LIMIT} photos max.`);
      } else {
        toast.error(String(e?.detail?.message ?? e?.detail ?? 'Upload failed'));
      }
    },
  });

  const reorderMut = useMutation({
    mutationFn: (order) => api.ownerGalleryReorder(token, brandId, order),
    onSuccess: () => refetch(),
    onError: (e) => toast.error(String(e?.detail?.message ?? 'Reorder failed')),
  });

  const toggleMut = useMutation({
    mutationFn: ({ photoId, isActive }) =>
      api.ownerGallerySetActive(token, brandId, photoId, isActive),
    onSuccess: () => refetch(),
    onError: (e) => toast.error(String(e?.detail?.message ?? 'Update failed')),
  });

  const deleteMut = useMutation({
    mutationFn: (photoId) => api.ownerGalleryDelete(token, brandId, photoId),
    onSuccess: () => { toast.success('Photo deleted'); refetch(); },
    onError: (e) => toast.error(String(e?.detail?.message ?? 'Delete failed')),
  });

  const photos = galleryQ.data ?? [];
  const atLimit = photos.length >= GALLERY_LIMIT;

  const moveBy = (index, delta) => {
    const next = [...photos];
    const tgt = index + delta;
    if (tgt < 0 || tgt >= next.length) return;
    [next[index], next[tgt]] = [next[tgt], next[index]];
    reorderMut.mutate(next.map((p) => p.id));
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_MIMES.includes(file.type)) {
      toast.error('Only PNG, JPEG, or WEBP allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5 MB per photo.');
      return;
    }
    uploadMut.mutate(file);
  };

  if (galleryQ.isLoading) {
    return <div className="text-xs text-white/55">Loading gallery…</div>;
  }
  if (galleryQ.error) {
    return (
      <div className="text-xs text-rose-300">
        Failed to load gallery.{' '}
        <button className="underline" onClick={() => galleryQ.refetch()}>retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="gallery-section">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={onPickFile}
        className="hidden"
        data-testid="gallery-file-input"
      />

      {photos.length === 0 ? (
        <div className="text-xs text-white/55">
          No photos yet. Add up to {GALLERY_LIMIT} photos to bring your brand
          page to life — interior, food, ambiance.
        </div>
      ) : null}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {photos.map((p, idx) => (
          <div
            key={p.id}
            className={`relative rounded-xl overflow-hidden border border-white/10 bg-black/40 ${p.is_active ? '' : 'opacity-50'}`}
            data-testid={`gallery-photo-${idx}`}
          >
            <img
              src={p.image_url}
              alt=""
              className="w-full h-32 object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-black/60 flex items-center justify-between gap-1">
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  className="p-1 rounded hover:bg-white/10 disabled:opacity-30"
                  onClick={() => moveBy(idx, -1)}
                  disabled={idx === 0 || reorderMut.isPending}
                  data-testid={`gallery-up-${idx}`}
                  aria-label="Move up"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-white/10 disabled:opacity-30"
                  onClick={() => moveBy(idx, 1)}
                  disabled={idx === photos.length - 1 || reorderMut.isPending}
                  data-testid={`gallery-down-${idx}`}
                  aria-label="Move down"
                >
                  <ArrowDown size={14} />
                </button>
              </div>
              <div className="flex items-center gap-1">
                <label className="text-[10px] text-white/80 flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-gold w-3 h-3"
                    checked={!!p.is_active}
                    onChange={(e) =>
                      toggleMut.mutate({ photoId: p.id, isActive: e.target.checked })
                    }
                    data-testid={`gallery-active-${idx}`}
                  />
                  Active
                </label>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-rose-500/20 text-rose-300"
                  onClick={() => {
                    if (window.confirm('Delete this photo?')) {
                      deleteMut.mutate(p.id);
                    }
                  }}
                  disabled={deleteMut.isPending}
                  data-testid={`gallery-delete-${idx}`}
                  aria-label="Delete photo"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Add tile */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={atLimit || uploadMut.isPending}
          className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.05] h-32 flex flex-col items-center justify-center text-white/55 text-xs gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="gallery-add-tile"
        >
          <ImagePlus size={20} />
          {atLimit
            ? `${photos.length}/${GALLERY_LIMIT} — limit reached`
            : uploadMut.isPending
              ? 'Uploading…'
              : `Add Photo (${photos.length}/${GALLERY_LIMIT})`}
        </button>
      </div>
    </div>
  );
}
